/**
 * Compliance — TRUS-486 §1.9.
 *
 * Who owes which training, when it is due, and who is overdue.
 *
 * The screen is built around a number most dashboards would hide: **unevaluated**. A
 * requirement whose due date could not be computed — almost always a staff profile with
 * no hire date — is neither compliant nor overdue. It is unknown, and folding unknowns
 * into "fine" is how a compliance report becomes worse than no report. So it gets its own
 * tile, its own tone, and an explanation of how to fix it.
 */

import React, { useCallback, useEffect, useState } from 'react'
import { RefreshCw, ShieldCheck, AlertTriangle } from 'lucide-react'
import { PageHeader, PageContainer, PageState, SectionHeader, StatTile, StatTileGrid } from '@/components/weavein'
import { useOrgStaffOptions } from '@/components/forms/staffOptions'
import { Button, ErrorNote, FILTER_INPUT, FilterBar, Pill, SortTh, TableShell, Td, Th, useSort } from './ui'
import {
  Assignment,
  ComplianceSummary,
  complianceSummary,
  formatDate,
  listAssignments,
  runEvaluator,
  statusTone,
} from './api'
import { FOCUS_STAFF_KEY } from './deepLink'

const STATUS_LABEL: Record<Assignment['status'], string> = {
  overdue: 'Overdue',
  due_soon: 'Due soon',
  pending: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

/** Turns the stored basis into something an admin can act on. */
function explainBasis(a: Assignment): string {
  if (a.due_date) {
    switch (a.due_basis) {
      case 'hire':
        return `from hire date ${formatDate(a.due_basis_date)}`
      case 'role_change':
        return `from role change ${formatDate(a.due_basis_date)}`
      case 'last_completion':
        return `from last completion ${formatDate(a.due_basis_date)}`
      case 'fixed':
        return 'fixed date'
      case 'immediate':
        return 'due on assignment'
      case 'manual':
        return 'set by an administrator'
      default:
        return ''
    }
  }
  if (a.due_basis === 'hire_date_missing') return 'no hire date on this staff profile'
  if (a.due_basis === 'role_change_missing') return 'no role-change date on this staff profile'
  if (a.due_basis === 'fixed_date_invalid') return 'the training’s fixed date is not a valid date'
  return 'the schedule could not be evaluated'
}

export default function CompliancePage() {
  // TRUS-562: include org admins (CEO/Chief) so they show by name, not "Staff #<id>".
  const staffOptions = useOrgStaffOptions({ includeOrgAdmins: true })
  // List filters (TRUS-562: sort/filter in Training by staff).
  const [search, setSearch] = useState('')
  const [trainingFilter, setTrainingFilter] = useState('')
  const [mandatoryOnly, setMandatoryOnly] = useState(false)
  const [summary, setSummary] = useState<ComplianceSummary | null>(null)
  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [running, setRunning] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('')
  // Set by Staff Profiles when an admin follows a person's training link. One-shot:
  // read and cleared on mount, so a later visit to Compliance is unfiltered.
  const [focusUserId, setFocusUserId] = useState<number | null>(() => {
    try {
      const raw = localStorage.getItem(FOCUS_STAFF_KEY)
      if (!raw) return null
      localStorage.removeItem(FOCUS_STAFF_KEY)
      const parsed = parseInt(raw, 10)
      return Number.isFinite(parsed) ? parsed : null
    } catch {
      return null
    }
  })

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [s, a] = await Promise.all([
        complianceSummary(),
        listAssignments({
          open_only: !statusFilter && focusUserId == null,
          status: statusFilter || undefined,
          user_id: focusUserId ?? undefined,
        }),
      ])
      setSummary(s)
      setAssignments(a.assignments)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load compliance data.')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, focusUserId])

  useEffect(() => {
    void load()
  }, [load])

  const recompute = async () => {
    setRunning(true)
    setActionError(null)
    try {
      await runEvaluator()
      await load()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not recompute assignments.')
    } finally {
      setRunning(false)
    }
  }

  const staffName = (userId: number) =>
    staffOptions.find((o) => o.value === String(userId))?.label ?? `Staff #${userId}`

  const STATUS_RANK: Record<string, number> = { overdue: 0, due_soon: 1, pending: 2, completed: 3, cancelled: 4 }
  const filtered = assignments.filter((a) => {
    const needle = search.trim().toLowerCase()
    if (needle && !`${staffName(a.user_id)} ${a.training_title}`.toLowerCase().includes(needle)) return false
    if (trainingFilter && String(a.training_id) !== trainingFilter) return false
    if (mandatoryOnly && a.is_mandatory !== true) return false
    return true
  })
  const { sorted, sort, toggle } = useSort(
    filtered,
    {
      staff: (a) => staffName(a.user_id),
      training: (a) => a.training_title,
      status: (a) => STATUS_RANK[a.status] ?? 9,
      due: (a) => a.due_date,
      assigned: (a) => a.assigned_via,
    },
    { key: 'status', dir: 'asc' },
  )
  const assignmentTrainings = Array.from(
    new Map(assignments.map((a) => [a.training_id, a.training_title])),
    ([id, title]) => ({ id, title }),
  ).sort((x, y) => x.title.localeCompare(y.title))
  const filtersOn = Boolean(search || trainingFilter || mandatoryOnly || focusUserId != null || statusFilter)

  const byStatus = summary?.by_status ?? {}
  const overdue = byStatus.overdue ?? 0
  const dueSoon = byStatus.due_soon ?? 0
  const unevaluated = summary?.unevaluated ?? 0
  const nothingTracked = !loading && !error && assignments.length === 0 && overdue + dueSoon + unevaluated === 0

  return (
    <>
      <PageHeader
        eyebrow="Training · Compliance"
        title="Compliance"
        description="Who owes which training, and when it falls due."
        actions={
          <Button onClick={() => void recompute()} disabled={running}>
            <RefreshCw className={`h-4 w-4 ${running ? 'animate-spin' : ''}`} />
            {running ? 'Recomputing…' : 'Recompute now'}
          </Button>
        }
      />
      <PageContainer>
        <div className="space-y-4">
          {actionError && <ErrorNote message={actionError} onDismiss={() => setActionError(null)} />}

          {focusUserId != null && (
            <div className="flex items-center gap-2 rounded-[10px] border border-v2border bg-paper px-4 py-2 text-[13px] text-ink-700">
              <span>
                Showing every requirement for <strong>{staffName(focusUserId)}</strong>, completed ones included.
              </span>
              <button
                type="button"
                onClick={() => setFocusUserId(null)}
                className="ml-auto rounded-[var(--v2-r-sm)] px-2 py-1 text-[12px] text-ink-500 hover:bg-paper-2 hover:text-ink"
              >
                Show everyone
              </button>
            </div>
          )}

          {loading ? (
            <PageState variant="loading" title="Loading compliance" />
          ) : error ? (
            <PageState
              variant="error"
              title="Could not load compliance"
              message={error}
              action={<Button onClick={() => void load()}>Retry</Button>}
            />
          ) : nothingTracked ? (
            <PageState
              variant="empty"
              icon={<ShieldCheck className="h-5 w-5 text-ink-400" />}
              title="Nothing is being tracked yet"
              message="Assignments appear once trainings have a schedule and staff have hire dates on their profiles. Add a due schedule to a training in the catalogue, then load your staff roster."
              action={
                <Button onClick={() => void recompute()} disabled={running}>
                  <RefreshCw className="h-4 w-4" /> Recompute now
                </Button>
              }
            />
          ) : (
            <>
              <StatTileGrid>
                <StatTile label="Overdue" value={overdue} tone={overdue > 0 ? 'danger' : 'neutral'} />
                <StatTile label="Due soon" value={dueSoon} tone={dueSoon > 0 ? 'attention' : 'neutral'} />
                <StatTile label="Mandatory outstanding" value={summary?.mandatory_open ?? 0} tone="neutral" />
                <StatTile
                  label="Unevaluated"
                  value={unevaluated}
                  tone={unevaluated > 0 ? 'attention' : 'neutral'}
                  caption={unevaluated > 0 ? 'No due date could be computed' : 'All requirements evaluated'}
                />
              </StatTileGrid>

              {unevaluated > 0 && (
                <div className="flex items-start gap-3 rounded-[10px] border border-attention bg-attention-soft px-4 py-3 text-[13px] text-attention-ink">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <strong>{unevaluated}</strong>{' '}
                    {unevaluated === 1 ? 'requirement has' : 'requirements have'} no due date, so
                    {unevaluated === 1 ? ' that person is' : ' those people are'} neither compliant nor overdue —
                    just unknown. Almost always a missing <strong>Date of Hire</strong> on the staff profile. Fill
                    those in, then Recompute.
                  </div>
                </div>
              )}

              <FilterBar>
                <input
                  type="search"
                  className={`${FILTER_INPUT} min-w-[220px] flex-1`}
                  placeholder="Search staff or training"
                  aria-label="Search staff or training"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <select
                  className={FILTER_INPUT}
                  aria-label="Filter by staff member"
                  value={focusUserId != null ? String(focusUserId) : ''}
                  onChange={(e) => setFocusUserId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">All staff</option>
                  {staffOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <select className={FILTER_INPUT} aria-label="Filter by training" value={trainingFilter} onChange={(e) => setTrainingFilter(e.target.value)}>
                  <option value="">All trainings</option>
                  {assignmentTrainings.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.title}
                    </option>
                  ))}
                </select>
                <label className="flex items-center gap-1.5 text-[13px] text-ink-500">
                  <input type="checkbox" checked={mandatoryOnly} onChange={(e) => setMandatoryOnly(e.target.checked)} />
                  Mandatory only
                </label>
                <select
                  className={FILTER_INPUT}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filter by status"
                >
                  <option value="">Open requirements</option>
                  <option value="overdue">Overdue only</option>
                  <option value="due_soon">Due soon only</option>
                  <option value="pending">Scheduled only</option>
                  <option value="completed">Completed</option>
                </select>
                {filtersOn && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setSearch('')
                      setTrainingFilter('')
                      setMandatoryOnly(false)
                      setFocusUserId(null)
                      setStatusFilter('')
                    }}
                  >
                    Clear filters
                  </Button>
                )}
              </FilterBar>

              {sorted.length === 0 ? (
                <PageState variant="empty" title="Nothing matches these filters" />
              ) : (
                <>
                  <SectionHeader
                    title={
                      sorted.length === assignments.length
                        ? `${assignments.length} requirement${assignments.length === 1 ? '' : 's'}`
                        : `${sorted.length} of ${assignments.length} requirements`
                    }
                  />
                  <TableShell>
                    <thead>
                      <tr>
                        <SortTh label="Staff" sortKey="staff" sort={sort} onSort={toggle} />
                        <SortTh label="Training" sortKey="training" sort={sort} onSort={toggle} />
                        <SortTh label="Status" sortKey="status" sort={sort} onSort={toggle} />
                        <SortTh label="Due" sortKey="due" sort={sort} onSort={toggle} />
                        <Th>Why</Th>
                        <SortTh label="Assigned" sortKey="assigned" sort={sort} onSort={toggle} />
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((a) => (
                        <tr key={a.id} className="hover:bg-paper-2">
                          <Td className="font-medium">{staffName(a.user_id)}</Td>
                          <Td>
                            {a.training_title}
                            {a.is_mandatory === true && (
                              <span className="ml-2">
                                <Pill tone="attention">Mandatory</Pill>
                              </span>
                            )}
                          </Td>
                          <Td>
                            <Pill tone={statusTone(a.status)}>{STATUS_LABEL[a.status]}</Pill>
                          </Td>
                          <Td className="whitespace-nowrap">
                            {a.due_date ? formatDate(a.due_date) : <span className="text-ink-400">Not computed</span>}
                          </Td>
                          <Td className="text-ink-500">{explainBasis(a)}</Td>
                          <Td className="text-ink-500">
                            {a.assigned_via === 'rule' ? 'By rule' : a.assigned_via === 'role' ? 'By role' : 'By name'}
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </TableShell>
                </>
              )}

              {summary && summary.overdue_staff.length > 0 && (
                <>
                  <SectionHeader title="Staff with the most overdue" />
                  <TableShell>
                    <thead>
                      <tr>
                        <Th>Staff</Th>
                        <Th>Overdue</Th>
                        <Th>Earliest due date</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.overdue_staff.map((s) => (
                        <tr key={s.user_id} className="hover:bg-paper-2">
                          <Td className="font-medium">{staffName(s.user_id)}</Td>
                          <Td className="tabular-nums">{s.overdue_count}</Td>
                          <Td>{formatDate(s.earliest_due)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </TableShell>
                </>
              )}
            </>
          )}
        </div>
      </PageContainer>
    </>
  )
}
